import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";

const ArtistDetails = () => {
  const { artistId } = useParams();
  const navigate = useNavigate();
  const [artist, setArtist] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArtist = async () => {
      try {
        const artistRef = doc(db, "artists", artistId);
        const artistSnap = await getDoc(artistRef);

        if (artistSnap.exists()) {
          setArtist(artistSnap.data());
        } else {
          setError("Artiste non trouvé");
        }
      } catch (err) {
        console.error("Erreur lors de la récupération de l'artiste:", err);
        setError("Erreur lors du chargement des données");
      } finally {
        setIsLoading(false);
      }
    };

    if (artistId) {
      fetchArtist();
    }
  }, [artistId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#00263a]">
        <Header />
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          <div className="flex items-center justify-center min-h-[400px]">
            <p className="text-white text-xl">Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !artist) {
    return (
      <div className="min-h-screen bg-[#00263a]">
        <Header />
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          <div className="bg-red-800 rounded-lg shadow-md p-6 mb-6">
            <p className="text-white text-xl">{error || "Artiste non trouvé"}</p>
            <button
              onClick={() => navigate("/create-artist")}
              className="mt-4 bg-[#33c7b6] hover:bg-[#2ab3a3] text-[#00263a] px-6 py-2 rounded-full font-semibold"
            >
              Retour à la création
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#00263a]">
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-white">
            Artiste créé avec succès !
          </h1>
          <button
            onClick={() => navigate("/create-artist")}
            className="bg-[#33c7b6] hover:bg-[#2ab3a3] text-[#00263a] px-6 py-2 rounded-full font-semibold"
          >
            Créer un autre artiste
          </button>
        </div>

        {/* Informations de base */}
        <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Informations de base</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                Artist ID
              </label>
              <p className="text-white font-semibold">{artist.artistId}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                First Name
              </label>
              <p className="text-white">{artist.firstName}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                Last Name
              </label>
              <p className="text-white">{artist.lastName}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                Wallet Address
              </label>
              <p className="text-white font-mono text-sm break-all">{artist.walletAddress}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                Payment Splitter Address
              </label>
              <p className="text-white font-mono text-sm break-all">{artist.paymentSplitterAddress}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                Status
              </label>
              <span className={`inline-block px-3 py-1 rounded-full text-sm ${
                artist.status === "active" ? "bg-green-500" : "bg-gray-500"
              } text-white`}>
                {artist.status}
              </span>
            </div>
            {artist.statusPro && (
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Status Pro
                </label>
                <p className="text-white">{artist.statusPro}</p>
              </div>
            )}
            {artist.exhibitionId && (
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Exhibition ID
                </label>
                <p className="text-white">{artist.exhibitionId}</p>
              </div>
            )}
            {artist.slogan && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Slogan
                </label>
                <p className="text-white italic">{artist.slogan}</p>
              </div>
            )}
            {artist.creationTime && (
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-1">
                  Date de création
                </label>
                <p className="text-white">
                  {artist.creationTime?.toDate
                    ? artist.creationTime.toDate().toLocaleString("fr-FR")
                    : new Date(artist.creationTime).toLocaleString("fr-FR")}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Images */}
        {(artist.coverPicture || artist.profilePicture || artist.descriptionCoverPicture || 
          artist.imageVideoPresentation || artist.videoPresentation) && (
          <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold text-white mb-4">Images</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {artist.coverPicture && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Cover Picture
                  </label>
                  <img
                    src={artist.coverPicture}
                    alt="Cover"
                    className="w-full h-48 object-cover rounded-md"
                  />
                </div>
              )}
              {artist.descriptionCoverPicture && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Description Cover Picture
                  </label>
                  <img
                    src={artist.descriptionCoverPicture}
                    alt="Description Cover"
                    className="w-full h-48 object-cover rounded-md"
                  />
                </div>
              )}
              {artist.profilePicture && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Profile Picture
                  </label>
                  <img
                    src={artist.profilePicture}
                    alt="Profile"
                    className="w-full h-48 object-cover rounded-md"
                  />
                </div>
              )}
              {artist.imageVideoPresentation && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Image Video Presentation
                  </label>
                  <img
                    src={artist.imageVideoPresentation}
                    alt="Video Presentation"
                    className="w-full h-48 object-cover rounded-md"
                  />
                </div>
              )}
              {artist.videoPresentation && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Video Presentation
                  </label>
                  <video
                    src={artist.videoPresentation}
                    controls
                    className="w-full rounded-md"
                  >
                    Votre navigateur ne supporte pas la lecture vidéo.
                  </video>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Links */}
        {artist.links && Object.keys(artist.links).length > 0 && (
          <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold text-white mb-4">Liens</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {artist.links.facebook && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Facebook
                  </label>
                  <a
                    href={artist.links.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#33c7b6] hover:underline break-all"
                  >
                    {artist.links.facebook}
                  </a>
                </div>
              )}
              {artist.links.instagram && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Instagram
                  </label>
                  <a
                    href={artist.links.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#33c7b6] hover:underline break-all"
                  >
                    {artist.links.instagram}
                  </a>
                </div>
              )}
              {artist.links.twitter && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Twitter
                  </label>
                  <a
                    href={artist.links.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#33c7b6] hover:underline break-all"
                  >
                    {artist.links.twitter}
                  </a>
                </div>
              )}
              {artist.links.website && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    Website
                  </label>
                  <a
                    href={artist.links.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#33c7b6] hover:underline break-all"
                  >
                    {artist.links.website}
                  </a>
                </div>
              )}
              {artist.links.linkedin && (
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">
                    LinkedIn
                  </label>
                  <a
                    href={artist.links.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#33c7b6] hover:underline break-all"
                  >
                    {artist.links.linkedin}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* More About Sections */}
        {artist.moreAbout && Object.keys(artist.moreAbout).length > 0 && (
          <div className="bg-gray-800 rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold text-white mb-4">More About</h2>
            <div className="space-y-6">
              {Object.entries(artist.moreAbout).map(([sectionKey, section], index) => (
                <div key={sectionKey} className="bg-gray-700 rounded-md p-4">
                  <h3 className="text-lg font-semibold text-white mb-3">
                    Section {index + 1}
                  </h3>
                  
                  {/* Type: Description */}
                  {section.desctiption !== undefined && (
                    <div className="space-y-3">
                      {section.title && (
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-1">
                            Title
                          </label>
                          <p className="text-white">{section.title}</p>
                        </div>
                      )}
                      {section.desctiption && (
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-1">
                            Description
                          </label>
                          <div
                            className="text-white"
                            dangerouslySetInnerHTML={{ __html: section.desctiption }}
                          />
                        </div>
                      )}
                      {section.img && (
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">
                            Image
                          </label>
                          <img
                            src={section.img}
                            alt={section.title || "Section image"}
                            className="w-full max-w-md h-auto rounded-md"
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Type: Left/Right */}
                  {section.leftTitle !== undefined && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        {section.leftTitle && (
                          <div className="mb-2">
                            <label className="block text-sm font-medium text-gray-400 mb-1">
                              Left Title
                            </label>
                            <p className="text-white font-semibold">{section.leftTitle}</p>
                          </div>
                        )}
                        {section.leftText && (
                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">
                              Left Text
                            </label>
                            <div
                              className="text-white"
                              dangerouslySetInnerHTML={{ __html: section.leftText }}
                            />
                          </div>
                        )}
                      </div>
                      <div>
                        {section.rightTitle && (
                          <div className="mb-2">
                            <label className="block text-sm font-medium text-gray-400 mb-1">
                              Right Title
                            </label>
                            <p className="text-white font-semibold">{section.rightTitle}</p>
                          </div>
                        )}
                        {section.rightText && (
                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">
                              Right Text
                            </label>
                            <div
                              className="text-white"
                              dangerouslySetInnerHTML={{ __html: section.rightText }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Type: Events */}
                  {section.events !== undefined && (
                    <div className="space-y-3">
                      {section.title && (
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-1">
                            Title
                          </label>
                          <p className="text-white font-semibold">{section.title}</p>
                        </div>
                      )}
                      {section.img && (
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">
                            Image
                          </label>
                          <img
                            src={section.img}
                            alt={section.title || "Events image"}
                            className="w-full max-w-md h-auto rounded-md"
                          />
                        </div>
                      )}
                      {section.events && section.events.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-2">
                            Events
                          </label>
                          <div className="space-y-2">
                            {section.events.map((event, eventIndex) => (
                              <div
                                key={eventIndex}
                                className="bg-gray-600 rounded-md p-3 flex justify-between items-center"
                              >
                                <span className="text-white">{event.text}</span>
                                <span className="text-gray-300 font-semibold">{event.year}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bouton retour */}
        <div className="flex justify-center">
          <button
            onClick={() => navigate("/create-artist")}
            className="bg-[#33c7b6] hover:bg-[#2ab3a3] text-[#00263a] px-8 py-3 rounded-full font-semibold transition-colors"
          >
            Créer un autre artiste
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArtistDetails;

